import React, { useEffect, useId, useRef, useState } from "react"
import { Box } from "@mui/material"

import { dsColorNew } from "@palette"


/**
 * An announcement body inside a sandboxed iframe that grows to fit its content (ported from retail).
 *
 * The body is HTML written by a person and stored in the database, so it is treated as untrusted.
 * The sandbox carries `allow-scripts` but NEVER `allow-same-origin`: together they cancel the sandbox
 * and turn any announcement into stored XSS against everyone who opens the popup. On its own,
 * `allow-scripts` leaves the document in an opaque origin — no cookies, no storage, no app DOM, no
 * navigating the tab. What it buys is the height report below; without it the frame had a fixed
 * height and a two-line announcement left the dialog mostly empty. The bodies are written by our own
 * staff in News Management, which is why the trade is acceptable.
 */

// Namespaced: `postMessage` with "*" is a shared channel and other widgets post into the window
const HEIGHT_MESSAGE = "ds-announcement-height"

// A broken layout must not produce a ten-thousand-pixel dialog, and an empty one stays clickable
const MIN_HEIGHT = 80
const MAX_HEIGHT = 4000

const heightReporter = frameId => `
	<script>
		(function () {
			var id = ${JSON.stringify(frameId)};
			var last = 0;
			function measure() {
				var body = document.body;
				if (!body) return 0;
				var styles = window.getComputedStyle(body);
				return Math.ceil(body.scrollHeight + parseFloat(styles.marginTop || 0) + parseFloat(styles.marginBottom || 0));
			}
			function send() {
				var height = measure();
				// a dead band, so a sub-pixel reflow cannot start a resize loop
				if (!height || Math.abs(height - last) < 2) return;
				last = height;
				parent.postMessage({ type: ${JSON.stringify(HEIGHT_MESSAGE)}, id: id, height: height }, "*");
			}
			function start() {
				send();
				if (window.ResizeObserver) new ResizeObserver(send).observe(document.body);
			}
			if (document.body) start();
			else document.addEventListener("DOMContentLoaded", start);
			// images land after DOMContentLoaded and change the height when they do; once they are in,
			// the parent is told the document is ready (the popup waits for it before showing)
			window.addEventListener("load", function () {
				send();
				parent.postMessage({ type: ${JSON.stringify(HEIGHT_MESSAGE)}, id: id, height: measure(), loaded: true }, "*");
			});
		})();
	</script>
`

// Whoever writes the body pastes markup, not CSS, so plain elements come out right on their own:
// element selectors only, in the app's font and colours
const BASE_STYLES = `
	<link rel="preconnect" href="https://fonts.googleapis.com">
	<link href="https://fonts.googleapis.com/css2?family=Roboto:wght@400;500;600;700&display=swap" rel="stylesheet">
	<base target="_blank">
	<style>
		:root { color-scheme: light; }
		* { box-sizing: border-box; }
		body {
			margin: 0; padding: 24px;
			font-family: "Roboto", "Helvetica", "Arial", sans-serif; font-size: 15px; line-height: 1.65;
			color: ${dsColorNew.textDark}; -webkit-font-smoothing: antialiased;
		}
		body > :first-child { margin-top: 0; }
		body > :last-child { margin-bottom: 0; }
		h1, h2, h3, h4 { margin: 1.6em 0 0.5em; line-height: 1.3; font-weight: 600; color: ${dsColorNew.textTitle}; }
		h1 { font-size: 24px; } h2 { font-size: 19px; } h3 { font-size: 16px; } h4 { font-size: 15px; }
		p { margin: 0 0 1em; }
		small { font-size: 13px; color: ${dsColorNew.textMuted}; }
		a { color: ${dsColorNew.appBlue}; text-decoration: underline; text-underline-offset: 2px; }
		a:hover { color: ${dsColorNew.appBlueHover}; }
		/* a link alone in its paragraph is a call to action: the app's blue button */
		p > a:only-child {
			display: inline-block; padding: 10px 20px; border-radius: 8px;
			background: ${dsColorNew.appBlue}; color: ${dsColorNew.white}; font-weight: 600; text-decoration: none;
		}
		p > a:only-child:hover { background: ${dsColorNew.appBlueHover}; color: ${dsColorNew.white}; }
		img { display: block; max-width: 100%; height: auto; border-radius: 6px; }
		figure { margin: 1.25em 0; }
		figcaption { margin-top: 0.5em; font-size: 13px; color: ${dsColorNew.textMuted}; }
		/* an opening image leads edge to edge under the dialog header */
		body > img:first-child, body > figure:first-child > img, body > p:first-child:has(> img:only-child) > img {
			width: calc(100% + 48px); max-width: none; margin: -24px -24px 20px -24px; border-radius: 0;
		}
		body > figure:first-child, body > p:first-child:has(> img:only-child) { margin-top: 0; }
		mark { padding: 0.05em 0.25em; border-radius: 3px; background: ${dsColorNew.noteAmberBg}; color: ${dsColorNew.noteAmber}; }
		details {
			margin: 0 0 1em; padding: 10px 14px; border: 1px solid ${dsColorNew.grayBorder};
			border-radius: 8px; background: ${dsColorNew.filterInputBg};
		}
		details[open] { padding-bottom: 14px; }
		summary { cursor: pointer; font-weight: 600; color: ${dsColorNew.textDark}; }
		summary::marker { color: ${dsColorNew.textSubtle}; }
		kbd {
			padding: 0.1em 0.4em; border: 1px solid ${dsColorNew.grayBorder}; border-bottom-width: 2px; border-radius: 4px;
			background: ${dsColorNew.white}; font-family: ui-monospace, SFMono-Regular, Menlo, monospace; font-size: 0.85em;
		}
		ul, ol { margin: 0 0 1em; padding-left: 1.35em; }
		li { margin-bottom: 0.35em; }
		blockquote { margin: 1.25em 0; padding: 0.25em 0 0.25em 1em; border-left: 3px solid ${dsColorNew.grayBorder}; color: ${dsColorNew.textMuted}; }
		hr { margin: 1.75em 0; border: 0; border-top: 1px solid ${dsColorNew.grayBorder}; }
		code {
			padding: 0.15em 0.35em; border-radius: 4px; background: ${dsColorNew.pillTrackBg};
			font-family: ui-monospace, SFMono-Regular, Menlo, monospace; font-size: 0.9em;
		}
		pre { margin: 0 0 1em; padding: 12px; border-radius: 6px; background: ${dsColorNew.pillTrackBg}; overflow-x: auto; }
		pre code { padding: 0; background: none; }
		table { width: 100%; margin: 0 0 1em; border-collapse: collapse; font-size: 13px; }
		th, td { padding: 8px 10px; border: 1px solid ${dsColorNew.grayBorder}; text-align: left; }
		th { background: ${dsColorNew.tableHead}; font-weight: 600; color: ${dsColorNew.white}; }
	</style>
`

function buildDocument(html, frameId) {
	if (!html) return ""

	const head = `${BASE_STYLES}${heightReporter(frameId)}`

	// A full document brings its own <head>: the base styles go FIRST inside it, so whatever the
	// document declares wins over them
	if (/<html[\s>]/i.test(html)) {
		if (/<head[\s>]/i.test(html)) return html.replace(/<head([^>]*)>/i, `<head$1>${head}`)
		return html.replace(/<html([^>]*)>/i, `<html$1><head>${head}</head>`)
	}

	return `<!DOCTYPE html><html><head><meta charset="utf-8">${head}</head><body>${html}</body></html>`
}

// A dialog waiting for the frame shows itself at most this long after the first measurement (the full
// load can wait on a remote image), and never later than READY_TIMEOUT_MS
const READY_AFTER_MEASURE_MS = 300
const READY_TIMEOUT_MS = 1500

// `height` is the starting point before the document reports its own; `fixedHeight` keeps a constant
// panel instead (the editor's preview, so the form does not jump while someone types). `onReady(height)`
// fires once per document: when it has loaded, READY_AFTER_MEASURE_MS after its first measurement, or
// after READY_TIMEOUT_MS, whichever comes first.
export default function AppNewsFrame({ html, title = "Announcement", height = 200, bordered = false, fixedHeight = false, onReady, sx = {} }) {
	// each frame tags its messages: the popup and the preview can be mounted at once
	const frameId = useId()
	const frameRef = useRef(null)

	// debounced, so typing HTML by hand does not reload the frame on every key; `version` remounts it
	const [{ doc, version }, setDoc] = useState({ doc: buildDocument(html, frameId), version: 0 })
	const [measured, setMeasured] = useState(null)

	// Only a different document remounts the frame: remounting the same one on mount reloaded it a
	// second time 200 ms after opening, and the dialog visibly jumped twice
	useEffect(() => {
		const timer = setTimeout(() => setDoc(previous => {
			const next = buildDocument(html, frameId)
			return next === previous.doc ? previous : { doc: next, version: previous.version + 1 }
		}), 200)
		return () => clearTimeout(timer)
	}, [html, frameId])

	const onReadyRef = useRef(onReady)
	onReadyRef.current = onReady
	const readyFired = useRef(false)
	const measureTimer = useRef(null)
	const fireReady = reported => {
		if (readyFired.current) return
		readyFired.current = true
		onReadyRef.current?.(reported)
	}

	useEffect(() => {
		readyFired.current = false
		const timer = setTimeout(() => fireReady(null), READY_TIMEOUT_MS)
		return () => {
			clearTimeout(timer)
			clearTimeout(measureTimer.current)
			measureTimer.current = null
		}
	}, [version])

	useEffect(() => {
		if (fixedHeight) return undefined

		const onMessage = event => {
			// without allow-same-origin every frame's origin is "null" and proves nothing; the sending
			// window does: only our own iframe is this exact contentWindow
			if (event.source !== frameRef.current?.contentWindow) return
			if (event.data?.type !== HEIGHT_MESSAGE || event.data?.id !== frameId) return
			const reported = Number(event.data.height)
			if (!Number.isFinite(reported)) return
			const clamped = Math.min(Math.max(reported, MIN_HEIGHT), MAX_HEIGHT)
			setMeasured(clamped)
			if (event.data.loaded) fireReady(clamped)
			else if (!measureTimer.current) measureTimer.current = setTimeout(() => fireReady(clamped), READY_AFTER_MEASURE_MS)
		}

		window.addEventListener("message", onMessage)
		return () => window.removeEventListener("message", onMessage)
	}, [frameId, fixedHeight])

	// a new document is a new size
	useEffect(() => { setMeasured(null) }, [version])

	return <Box sx={{
		...(bordered && { border: `1px solid ${dsColorNew.grayBorder}`, borderRadius: '8px', backgroundColor: dsColorNew.white }),
		overflow: 'hidden',
		...sx,
	}}>
		<iframe
			key={version}
			ref={frameRef}
			title={title}
			srcDoc={doc}
			// allow-scripts WITHOUT allow-same-origin. Never both: see the note at the top of this file
			sandbox="allow-scripts allow-popups allow-popups-to-escape-sandbox"
			style={{ display: 'block', width: '100%', height: measured ?? height, border: 0, transition: 'height .15s ease' }}
		/>
	</Box>
}
